import React, {useState, useEffect, useCallback, forwardRef} from 'react';
import {TextField, Autocomplete, CircularProgress} from '@mui/material';
import {throttle} from 'lodash';
import algoliasearch from 'algoliasearch';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {Controller, useFormContext} from 'react-hook-form';
import {getFirestore, doc, getDoc} from 'firebase/firestore';

import firebaseApp from '../firebase.js';

const MinLength = 3;

const UncontroledComponent = forwardRef((props, ref) => {
    const {value: rawValue, onChange, disabled, ...rest} = props;

    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('Start typing to search for a user');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const db = getFirestore(firebaseApp);

    const client = algoliasearch('V0HTM0BKX4', 'ee12d8e047b5ed41f4a1820f671d14f2');
    const index = client.initIndex('usersIndex');

    const functions = getFunctions(firebaseApp);
    const getUser = httpsCallable(functions, 'getUser');

    let isSubscribed = true;

    const fetchOptions = useCallback(throttle(async(inputValue) => {
        const {hits} = await index.search(inputValue);
        const options = hits.map(hit => {
            const {objectID: id, displayName} = hit;

            return {
                id,
                displayName
            }
        });

        const userIds = options.map(option => option.id);
        const users = [];

        if (userIds.length) {
            for (const userId of userIds) {
                const userRef = doc(db, 'users', userId);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists) {
                    users.push({
                        id: userId,
                        ...userSnap.data()
                    });
                }
            }
        }

        if (isSubscribed) {
            const value = users.find(option => option.id === rawValue);
            if (!value) {
                setValue(null);
            }

            setOptions(users);
            setLoading(false);
        }
    }, 1000, {leading: false, trailing: true}), []);

    useEffect(() => {
        if (rawValue) {
            if (!value && isSubscribed) {
                setValue({id: rawValue});
                setInputValue(rawValue);
                setOptions([{id: rawValue}]);

                const findValue = async() => {
                    try {
                        const {data} = await getUser({user: rawValue});

                        if (data && isSubscribed) {
                            const {displayName} = data;
                            
                            setOptions([data]);
                            setValue(data)
                            setInputValue(displayName);
                        }
                    } catch(e) {
                        //
                    }
                    
                    if (isSubscribed) {
                        setLoading(false);
                    }
                };
                
                if (isSubscribed) {
                    findValue();
                    setLoading(true);
                }
            }
        }

        return () => isSubscribed = false;
    }, [rawValue]);

    useEffect(() => {
        if (!inputValue || inputValue === '') {
            setNoOptionsText('Start typing to search for a user');
        } else if (inputValue.length < MinLength) {
            setNoOptionsText(`Type at least ${MinLength} characters to search for a user`);
        } else {
            setNoOptionsText('No users found');
        }
    }, [inputValue]);

    return (
        <Autocomplete
            ref={ref}
            value={value}
            inputValue={inputValue}
            getOptionLabel={option => `${option.displayName || option.id} (${option.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            options={options}
            onChange={(e, newValue) => {
                setValue(newValue);
                onChange(e, newValue ? newValue.id : undefined);
            }}
            disabled={disabled}
            loading={loading}
            open={open}
            filterOptions={x => x}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            onInputChange={(e, newInputValue, reason) => {
                setInputValue(newInputValue);

                if (reason === 'input') {
                    if (newInputValue !== '' && newInputValue.length >= MinLength) {
                        setLoading(true);
                        fetchOptions(newInputValue);
                    } else {
                        setOptions([])
                    }
                } else if (reason === 'clear') {
                    setOptions([]);
                }
            }}
            noOptionsText={noOptionsText}
            renderInput={params => (
                <TextField
                    label="User"
                    {...params}
                    {...rest}
                     InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
});

export default props => {
    const {name, rules, onChange, ...rest} = props;

    const {control} = useFormContext();

    return (
        <Controller
            name={name}
            rules={rules}
            control={control}
            render={({field, fieldState: {error}}) => (
                <UncontroledComponent
                    {...field}
                    {...rest}
                    onChange={(e, option) => {
                        field.onChange(option);

                        if (onChange) {
                            onChange(option);
                        }
                    }}
                    error={!!error}
                    helperText={error ? error.message : null}
                />
            )}
        />
    );
};